import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar, Box, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, Select, MenuItem,
  InputAdornment, TablePagination, TableSortLabel, Snackbar,
  Popper, MenuList, ClickAwayListener, IconButton, Tooltip, Skeleton,
} from "@mui/material";
import { Alert } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import BusinessIcon from "@mui/icons-material/Business";
import DomainDisabledIcon from "@mui/icons-material/DomainDisabled";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  fetchCompanies,
  changeCompanyStatus,
} from "../redux/companies.action";
import { resetCompanies, clearError } from "../redux/companies.slice";
import CompanyDetailsDialog from "../components/CompanyDetailsDialog";
import CompanyChangeStatusDialog from "../components/CompanyChangeStatusDialog";
import CompanyAccessRightDialog from "../components/CompanyAccessRightDialog";
import CompanyAssignSnDialog from "../components/CompanyAssignSnDialog";

const tokens = {
  brand: "#3A4D54",
  brandHover: "#2C3B41",
  brandSoft: "rgba(58,77,84,0.06)",
  text: "#1F2A2F",
  textDim: "#5C6D74",
  textMuted: "#98A2B3",
  border: "#E8ECEE",
  surface: "#FFFFFF",
  rowHover: "#F7F9FA",
  successBg: "#ECFDF3",
  successFg: "#067647",
  dangerBg: "#FEF3F2",
  dangerFg: "#B42318",
  warningFg: "#B54708",
  fieldHeight: 38,
  rowHeight: 64,
};

const formatTitleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const useStyles = makeStyles(() => ({
  pageCard: {
    backgroundColor: tokens.surface,
    borderRadius: 6,
    minHeight: "calc(100vh - 160px)",
    padding: "28px 32px 12px",
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gridAutoRows: "min-content",
    alignContent: "start",
  },
  pageHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  pageTitle: {
    fontWeight: 600,
    fontSize: 20,
    letterSpacing: "-0.01em",
    color: tokens.text,
  },
  pageSubtitle: {
    fontSize: 13,
    color: tokens.textDim,
    fontVariantNumeric: "tabular-nums",
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  search: { flex: "1 1 0%", minWidth: 120 },
  selectField: { minWidth: 110, flexShrink: 0 },
  field: {
    "& .MuiOutlinedInput-root": {
      height: tokens.fieldHeight,
      fontSize: 14,
      backgroundColor: tokens.surface,
      "& fieldset": { borderColor: tokens.border },
      "&:hover fieldset": { borderColor: tokens.brand },
      "&.Mui-focused fieldset": { borderColor: tokens.brand, borderWidth: 1 },
    },
  },
  primaryBtn: {
    backgroundColor: tokens.brand,
    color: "#fff",
    height: tokens.fieldHeight,
    paddingInline: 18,
    boxShadow: "none",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": { backgroundColor: tokens.brandHover, boxShadow: "none" },
  },
  ghostBtn: {
    color: tokens.textDim,
    height: tokens.fieldHeight,
    textTransform: "none",
    fontWeight: 500,
    "&:hover": { backgroundColor: tokens.brandSoft, color: tokens.brand },
  },
  tableContainer: { border: "none", borderRadius: 0, boxShadow: "none" },
  table: {
    "& .MuiTableCell-root": {
      borderBottomColor: tokens.border,
      fontSize: 14,
      color: tokens.text,
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      whiteSpace: "nowrap",
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.02em",
      color: tokens.textDim,
      textTransform: "uppercase",
      backgroundColor: tokens.surface,
      paddingTop: 14,
      paddingBottom: 14,
      borderBottom: `1px solid ${tokens.border}`,
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 160ms cubic-bezier(0.22, 1, 0.36, 1)",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: tokens.rowHover,
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      height: tokens.rowHeight,
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  numberCell: {
    color: tokens.textMuted,
    fontVariantNumeric: "tabular-nums",
    width: 48,
  },
  companyCell: {
    minWidth: 220,
  },
  companyAvatar: {
    width: 36,
    height: 36,
    flexShrink: 0,
    fontSize: 14,
    backgroundColor: tokens.brandSoft,
    color: tokens.brand,
    border: `1px solid ${tokens.border}`,
  },
  companyName: {
    whiteSpace: "nowrap",
    fontWeight: 500,
    color: tokens.text,
  },
  planCell: {
    whiteSpace: "nowrap",
  },
  planError: {
    color: tokens.warningFg,
  },
  dimCell: {
    color: tokens.textMuted,
  },
  statusChip: {
    fontWeight: 500,
    fontSize: 12,
    height: 24,
    textTransform: "capitalize",
    borderRadius: 12,
    paddingInline: 4,
  },
  statusApproved: { backgroundColor: tokens.successBg, color: tokens.successFg },
  statusDeactivated: { backgroundColor: tokens.dangerBg, color: tokens.dangerFg },
  statusDefault: { backgroundColor: "#F2F4F7", color: tokens.textDim },
  actionBtn: {
    width: 32,
    height: 32,
    color: tokens.textDim,
    borderRadius: 6,
    "&:hover": { backgroundColor: tokens.brandSoft, color: tokens.brand },
  },
  menuPaper: {
    backgroundColor: tokens.surface,
    color: tokens.text,
    borderRadius: 6,
    border: `1px solid ${tokens.border}`,
    boxShadow: "0 8px 24px -10px rgba(15, 23, 27, 0.18), 0 2px 6px -2px rgba(15, 23, 27, 0.08)",
    minWidth: 200,
    paddingBlock: 4,
  },
  menuItem: {
    fontSize: 13,
    color: tokens.text,
    paddingBlock: 8,
    "&:hover": { backgroundColor: tokens.brandSoft },
  },
  emptyCell: {
    paddingBlock: 64,
  },
  emptyWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    color: tokens.textDim,
  },
  emptyIcon: {
    width: 44,
    height: 44,
    color: tokens.textMuted,
    backgroundColor: tokens.brandSoft,
    borderRadius: "50%",
    padding: 10,
  },
  skeletonRow: {
    "& .MuiTableCell-root": {
      borderBottomColor: tokens.border,
    },
  },
  pagination: {
    "& .MuiTablePagination-toolbar": {
      paddingInline: 0,
      color: tokens.textDim,
      fontSize: 13,
    },
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      fontSize: 13,
      color: tokens.textDim,
    },
  },
}));

const TYPE_OPTIONS = [
  { label: "Company Name", value: "companyName" },
  { label: "Reg No.", value: "regNo" },
];
const STATUS_OPTIONS = [
  { label: "All", value: "" },
  { label: "Approved", value: "approved" },
  { label: "Deactivated", value: "deactivated" },
];
const PLAN_OPTIONS = [
  { label: "All", value: "" },
  { label: "Pro", value: "pro" },
  { label: "Lite", value: "lite" },
];
// MUI hides the sort icon until a column is sorted, so nothing told admins Expires can be
// sorted. While unsorted it shows a faint two-way arrow instead, darkening on hover.
const UNSORTED_HINT = {
  "& .MuiTableSortLabel-icon": { opacity: 0.6 },
  "&:hover .MuiTableSortLabel-icon": { opacity: 1 },
};

export default function CompaniesListPage() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { data, totalRecords, isLoading, allBranchWhiteLabelCount, error } = useSelector((state) => state.companies);

  const [search, setSearch] = useState("");
  const [searchType, setSearchType] = useState("companyName");
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // null = the server's default order; otherwise "asc" | "desc" on the Expires column.
  const [expiredSort, setExpiredSort] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // Action menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const menuOpen = Boolean(anchorEl);

  // Dialog states
  const [viewCompany, setViewCompany] = useState(null);
  const [changeStatusDialog, setChangeStatusDialog] = useState({ open: false, action: "enable", companyId: null });
  const [accessRightCompany, setAccessRightCompany] = useState(null);
  const [assignSnCompany, setAssignSnCompany] = useState(null);

  const loadCompanies = (overrides = {}) => {
    const params = {
      page: overrides.page ?? page,
      length: overrides.length ?? rowsPerPage,
      plan: overrides.plan ?? planFilter,
      status: overrides.status ?? statusFilter,
      expiredSort: overrides.expiredSort ?? expiredSort,
    };
    if (searchType === "companyName") params.companyName = overrides.search ?? search;
    else params.regNo = overrides.search ?? search;
    dispatch(fetchCompanies(params));
  };

  useEffect(() => {
    loadCompanies();
    return () => { dispatch(resetCompanies()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleSearch = () => { setPage(0); loadCompanies({ page: 0 }); };
  const handleClear = () => {
    setSearch(""); setSearchType("companyName"); setStatusFilter(""); setPlanFilter(""); setPage(0);
    setExpiredSort(null);
    dispatch(fetchCompanies({ page: 0, length: rowsPerPage }));
  };
  // First click puts the soonest expiry first; each click after that flips the direction.
  const handleExpiredSort = () => {
    const next = expiredSort === "asc" ? "desc" : "asc";
    setExpiredSort(next); setPage(0);
    loadCompanies({ page: 0, expiredSort: next });
  };
  const handlePageChange = (e, newPage) => { setPage(newPage); loadCompanies({ page: newPage }); };
  const handleRowsPerPageChange = (e) => {
    const n = parseInt(e.target.value, 10); setRowsPerPage(n); setPage(0);
    loadCompanies({ page: 0, length: n });
  };

  const handleMenuOpen = (event, row) => { setAnchorEl(event.currentTarget); setSelectedRow(row); };
  const handleMenuClose = () => { setAnchorEl(null); };

  const handleChangeStatus = async (action, remark) => {
    const statusMap = { enable: "approved", disable: "deactivated" };
    const result = await dispatch(changeCompanyStatus(changeStatusDialog.companyId, statusMap[action], remark));
    setChangeStatusDialog({ open: false, action: "enable", companyId: null });
    if (result.success) {
      setSnack({ open: true, message: "Status updated successfully", severity: "success" });
      loadCompanies();
    } else {
      setSnack({ open: true, message: result.message, severity: "error" });
    }
  };

  const getStatusClass = (status) => {
    if (status === "approved") return classes.statusApproved;
    if (status === "deactivated") return classes.statusDeactivated;
    return classes.statusDefault;
  };

  const actionItems = selectedRow ? [
    { label: "View/Edit Company Details...", onClick: () => setViewCompany(selectedRow) },
    { label: "Access Rights (Deprecated)", onClick: () => setAccessRightCompany(selectedRow) },
    { label: "Assign", onClick: () => setAssignSnCompany(selectedRow) },
    ...(selectedRow.company.status === "deactivated" ? [{
      label: "Enable",
      onClick: () => setChangeStatusDialog({ open: true, action: "enable", companyId: selectedRow.company.id }),
      style: { color: "#ff0000" },
    }] : []),
    ...(selectedRow.company.status === "approved" ? [{
      label: "Disable",
      onClick: () => setChangeStatusDialog({ open: true, action: "disable", companyId: selectedRow.company.id }),
      style: { color: "#ff0000" },
    }] : []),
  ] : [];

  const isPlanError = (plan = "") =>
    plan.includes("Full Saas") && !["Pro", "Biz", "Lite"].some((p) => plan.includes(p));

  return (
    <Box className={classes.pageCard}>
      <Box className={classes.pageHeader}>
        <Typography className={classes.pageTitle} component="h1">Companies</Typography>
        {!isLoading && (
          <Typography className={classes.pageSubtitle}>
            {totalRecords.toLocaleString()} {totalRecords === 1 ? "company" : "companies"}
          </Typography>
        )}
      </Box>

      <Box className={classes.filterRow}>
        <Select variant="outlined" size="small" value={searchType}
          onChange={(e) => setSearchType(e.target.value)}
          className={`${classes.selectField} ${classes.field}`}>
          {TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
        <TextField className={`${classes.search} ${classes.field}`} variant="outlined" size="small"
          placeholder={searchType === "companyName" ? "Search company name" : "Search registration no."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" htmlColor={tokens.textMuted} /></InputAdornment> }}
        />
        <Select variant="outlined" size="small" value={planFilter} displayEmpty
          onChange={(e) => setPlanFilter(e.target.value)}
          className={`${classes.selectField} ${classes.field}`}
          renderValue={(v) => PLAN_OPTIONS.find((o) => o.value === v)?.label || <span style={{ color: tokens.textMuted }}>Plan</span>}>
          {PLAN_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label || "All plans"}</MenuItem>)}
        </Select>
        <Select variant="outlined" size="small" value={statusFilter} displayEmpty
          onChange={(e) => setStatusFilter(e.target.value)}
          className={`${classes.selectField} ${classes.field}`}
          renderValue={(v) => STATUS_OPTIONS.find((o) => o.value === v)?.label || <span style={{ color: tokens.textMuted }}>Status</span>}>
          {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
        <Button variant="contained" disableElevation onClick={handleSearch} className={classes.primaryBtn}>
          Search
        </Button>
        <Button variant="text" onClick={handleClear} startIcon={<ClearIcon fontSize="small" />} className={classes.ghostBtn}>
          Clear
        </Button>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => dispatch(clearError())}>{error}</Alert>
      )}

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.numberCell}>No.</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Reg. No.</TableCell>
              <TableCell>White Label</TableCell>
              <TableCell align="right">Branch WL</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell sortDirection={expiredSort || false}>
                <TableSortLabel active={Boolean(expiredSort)} direction={expiredSort || "asc"}
                  IconComponent={expiredSort ? undefined : SwapVertIcon}
                  sx={expiredSort ? undefined : UNSORTED_HINT}
                  onClick={handleExpiredSort}>
                  Expires
                </TableSortLabel>
              </TableCell>
              <TableCell>Deactivated</TableCell>
              <TableCell>Feature Group</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ width: 64 }}>{""}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`} className={classes.skeletonRow}>
                  <TableCell className={classes.numberCell}><Skeleton variant="text" width={20} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Skeleton variant="circular" width={36} height={36} />
                      <Skeleton variant="text" width={140} />
                    </Box>
                  </TableCell>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell align="right"><Skeleton variant="text" width={20} sx={{ ml: "auto" }} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="text" width={70} /></TableCell>
                  <TableCell><Skeleton variant="text" width={70} /></TableCell>
                  <TableCell><Skeleton variant="text" width={90} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={70} height={20} sx={{ borderRadius: 12 }} /></TableCell>
                  <TableCell align="right"><Skeleton variant="circular" width={20} height={20} sx={{ ml: "auto" }} /></TableCell>
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" className={classes.emptyCell}>
                  <Box className={classes.emptyWrap}>
                    <DomainDisabledIcon className={classes.emptyIcon} />
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: tokens.text }}>
                        No companies match your filters
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: tokens.textDim, mt: 0.5 }}>
                        Try clearing the search or status filter.
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow key={row.company.id}>
                  <TableCell className={classes.numberCell}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell className={classes.companyCell}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Avatar
                        src={row.company.logo || undefined}
                        alt=""
                        className={classes.companyAvatar}
                      >
                        {!row.company.logo && <BusinessIcon fontSize="small" />}
                      </Avatar>
                      <Box component="span" className={classes.companyName}>{row.company.name}</Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{row.company.regNo || <span className={classes.dimCell}>—</span>}</TableCell>
                  <TableCell>{row.company.whiteLabel && row.company.whiteLabel !== "-" ? row.company.whiteLabel : <span className={classes.dimCell}>—</span>}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{allBranchWhiteLabelCount[index] ?? 0}</TableCell>
                  <TableCell className={`${classes.planCell} ${isPlanError(row.company.plan) ? classes.planError : ""}`}>
                    {row.company.plan || <span className={classes.dimCell}>—</span>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {row.company.expired_at || <span className={classes.dimCell}>—</span>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {row.company.deactive_at || <span className={classes.dimCell}>—</span>}
                  </TableCell>
                  <TableCell>{row.company.feature_group || <span className={classes.dimCell}>—</span>}</TableCell>
                  <TableCell>
                    <Chip
                      label={formatTitleCase(row.company.status)}
                      size="small"
                      className={`${classes.statusChip} ${getStatusClass(row.company.status)}`}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Actions" arrow>
                      <IconButton
                        aria-label={`Actions for ${row.company.name}`}
                        size="small"
                        className={classes.actionBtn}
                        onClick={(e) => handleMenuOpen(e, row)}
                      >
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Popper open={menuOpen && selectedRow?.company.id === row.company.id} anchorEl={anchorEl}
                      placement="bottom-end" style={{ zIndex: 1300 }} modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}>
                      <ClickAwayListener onClickAway={handleMenuClose}>
                        <Paper className={classes.menuPaper}>
                          <MenuList dense>
                            {actionItems.map((item, i) => (
                              <MenuItem key={i} className={classes.menuItem} style={item.style}
                                onClick={() => { item.onClick(); handleMenuClose(); }}>
                                {item.label}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </Paper>
                      </ClickAwayListener>
                    </Popper>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {!isLoading && (
          <TablePagination component="div" count={totalRecords} page={page}
            onPageChange={handlePageChange} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            className={classes.pagination} />
        )}
      </TableContainer>
      {viewCompany && (
        <CompanyDetailsDialog
          selectedCompany={viewCompany}
          onClose={(didChange) => {
            setViewCompany(null);
            if (didChange) loadCompanies();
          }}
        />
      )}
      <CompanyChangeStatusDialog
        isOpen={changeStatusDialog.open}
        action={changeStatusDialog.action}
        handleConfirm={handleChangeStatus}
        handleClose={() => setChangeStatusDialog({ open: false, action: "enable", companyId: null })}
      />
      {accessRightCompany && (
        <CompanyAccessRightDialog
          selectedCompany={accessRightCompany.company}
          onClose={() => setAccessRightCompany(null)}
        />
      )}
      {assignSnCompany && (
        <CompanyAssignSnDialog
          selectedCompany={assignSnCompany}
          onClose={() => setAssignSnCompany(null)}
          onSuccess={() => {
            setAssignSnCompany(null);
            loadCompanies();
          }}
        />
      )}
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
